import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Link } from 'react-router-dom'
import { useMutation } from '@tanstack/react-query'
import { Clock4 } from 'lucide-react'
import { toast } from 'sonner'

import { apiErrorMessage, apiFetch } from '@/lib/api'
import Alert from '@/components/ui/Alert'
import Button from '@/components/ui/Button'
import Input from '@/components/ui/Input'
import Label from '@/components/ui/Label'

const schema = z.object({
  email: z
    .string()
    .min(1, 'Wpisz email')
    .email('Nieprawidłowy format email'),
})

type FormValues = z.infer<typeof schema>

export default function ForgotPassword() {
  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: '' },
  })

  const request = useMutation({
    mutationFn: (values: FormValues) =>
      apiFetch<{ message: string }>('/api/auth/password-reset-request', {
        method: 'POST',
        body: values,
      }),
    onSuccess: () => {
      toast.success('Jeśli konto istnieje, wysłaliśmy link na podany adres.')
      form.reset()
    },
  })

  const submitError = request.error ? apiErrorMessage(request.error) : null

  return (
    <main className="min-h-[100svh] flex items-center justify-center px-6 py-12">
      <div className="w-full max-w-[420px] rounded-card border border-border-subtle bg-bg-elevated p-8">
        <div className="mb-6 flex flex-col items-center text-center">
          <Clock4 className="mb-2 h-6 w-6 text-brand-accent" aria-hidden />
          <p className="text-xs uppercase tracking-widest text-text-faint">
            Dental Avenue
          </p>
          <h1 className="mt-1 text-2xl font-semibold">Resetowanie hasła</h1>
          <p className="mt-2 text-sm text-text-muted">
            Podaj email — jeśli konto istnieje, wyślemy link do ustawienia
            nowego hasła. Link jest ważny przez 60 minut.
          </p>
        </div>

        <form
          onSubmit={form.handleSubmit((v) => request.mutate(v))}
          noValidate
          className="space-y-4"
        >
          <div>
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              autoFocus
              invalid={!!form.formState.errors.email}
              {...form.register('email')}
            />
            {form.formState.errors.email && (
              <p className="mt-1 text-xs text-danger">
                {form.formState.errors.email.message}
              </p>
            )}
          </div>

          {submitError && <Alert variant="error">{submitError}</Alert>}

          <Button type="submit" loading={request.isPending} className="w-full">
            Wyślij link
          </Button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="text-sm text-text-muted hover:text-text-primary transition"
          >
            Wróć do logowania
          </Link>
        </div>
      </div>
    </main>
  )
}
